import { ReconciliationResult } from '../../types/optimistic.types';

/**
 * Universal Reconciliation Protocol
 * 
 * DESIGN RATIONALE:
 * - Centralizes the decision logic for mutation resolution (Confirm vs Revert).
 * - Interprets backend protocols (success envelopes, status codes) into reconciliation commands.
 * - Handles concurrency by validating sequence IDs where available.
 * - Platform-agnostic: No dependencies on browser APIs.
 */
export class ReconciliationProtocol {
    /**
     * Resolves a backend response into a standardized reconciliation result.
     * 
     * @param response The raw network response or error.
     * @returns A task-agnostic result directive.
     */
    public interpretResponse<T>(
        response: unknown
    ): ReconciliationResult<T> {
        // 1. Handle Empty/No-Content Success (e.g., 204 status)
        if (response === null || response === undefined) {
            return {
                success: true,
                data: undefined as unknown as T
            };
        }

        if (typeof response === 'object') {
            const potentialResponse = response as Record<string, unknown>;

            // 2. Handle Enveloped Success
            // Standard backend envelope: { success: true, data: T, metadata?: { sequenceId?: string } }
            if (potentialResponse.success === true) {
                const metadataObj = (potentialResponse.metadata as Record<string, unknown>) || {};
                return {
                    success: true,
                    data: (potentialResponse.data !== undefined ? potentialResponse.data : potentialResponse) as T,
                    serverSequence: metadataObj.sequenceId as string | undefined,
                };
            }

            // 3. Handle Explicit Error Objects (to avoid treating errors as unwrapped data)
            if (potentialResponse.errorCode || (potentialResponse.message && !potentialResponse.data)) {
                return {
                    success: false,
                    error: (response as Error) || new Error(String(potentialResponse.message || 'Mutation failed')),
                    conflictDetected: potentialResponse.status === 409 || potentialResponse.errorCode === 'CONFLICT'
                };
            }

            // 4. Handle Unwrapped Data (Middleware already extracted the 'data' payload)
            // If it's an object/array and doesn't look like an error, treat it as a success confirmation.
            return {
                success: true,
                data: response as T
            };
        }

        // 5. Handle Primitive Success (rare but possible)
        return {
            success: true,
            data: response as T
        };
    }

    /**
     * Identifies if a local state transition is "Stale" compared to a server sequence.
     */
    public isStale(localSequence: string, serverSequence: string): boolean {
        if (!serverSequence || !localSequence) return false;
        // Simple string comparison for now; can be upgraded to logical clocks (vector clocks) if needed.
        return localSequence !== serverSequence;
    }
}
